import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "border-amber-500/50 text-amber-400",
  approved: "border-emerald-500/50 text-emerald-400",
  rejected: "border-neutral-600 text-neutral-500",
};

export const RequestExerciseDialog = ({ open, onOpenChange, onApprovedSeen }) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/exercise-requests/mine"); setMine(data); } catch {}
    })();
  }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("What exercise is missing?"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/exercise-requests", { name: name.trim(), notes: notes.trim() || null });
      setMine((m) => [data, ...m]);
      setName(""); setNotes("");
      toast.success("Request sent — you'll see it in the library once approved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="request-ex-title" className="font-display text-2xl uppercase">Request an Exercise</DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            Missing something from the library? Suggest it and the coach can add it for everyone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Exercise name *</Label>
            <Input data-testid="request-ex-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pendulum Squat" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Notes (optional)</Label>
            <Textarea data-testid="request-ex-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Machine brand, variation, anything helpful…" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none text-white" />
          </div>
          <Button data-testid="request-ex-submit" onClick={submit} disabled={busy} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Request</>}
          </Button>

          {mine.length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">My Requests</div>
              <div className="divide-y divide-neutral-900 border border-neutral-800 max-h-48 overflow-y-auto">
                {mine.map((r) => (
                  <div key={r.request_id} data-testid={`my-request-${r.request_id}`} className="flex items-center gap-2 p-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{r.name}</div>
                      {r.notes && <div className="text-[10px] text-neutral-500 truncate">{r.notes}</div>}
                    </div>
                    <span className={`px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestExerciseDialog;
